/**
 * DOM manipulation and detecting helper functions
 *
 * @packageDocumentation
 */

import { ATTR_NAME } from 'constants/attr-name'
import { EventType, HtmlAttr, HTMLElementType } from 'constants/enums'
import { Nullable } from 'constants/types'

type HTMLAttributes = Record<string, string>
export type FormFields =
    | HTMLInputElement
    | HTMLSelectElement
    | HTMLTextAreaElement

/**
 * Referenced (page) types defined here. (Minimizes bundle size.)
 */
export enum PageType {
    index = 'index',
    category = 'category',
    story = 'story',
    search = 'search',
    videoCentre = 'video-centre',
    documentation = 'documentation',
    partnerships = 'partnerships',
    staticform = 'staticform',
    newsletters = 'newsletters'
}

/**
 * Promise for awaiting DOM.
 */
export const isDomReady = async (): Promise<void> => {
    return new Promise((resolve) =>
        document.readyState === 'loading'
            ? document.addEventListener(EventType.DOM_CONTENT_LOADED, () =>
                  resolve()
              )
            : resolve()
    )
}

/**
 * Add iFrame to the given position.
 *
 * @param {string} src iFrame src attribute
 * @param {HTMLElement} target Element to add
 * @param {HTMLAttributes} attributes
 */
export const addIframe = (
    src: string,
    target: HTMLElement,
    attributes?: HTMLAttributes
): HTMLIFrameElement => {
    const el = document.createElement(HTMLElementType.IFRAME)
    el.src = src.replace(/^(?:https?:)?(.+)$/, 'https:$1')
    if (attributes) {
        Object.keys(attributes).forEach((key: string) => {
            el.setAttribute(key, attributes[key])
        })
    }
    target.appendChild(el)
    return el
}

/**
 * Gets given element's real height includes its margin
 *
 * @param {HTMLElement} el
 * @return {number} Height of the element + top/bottom margin
 */
export const getOuterHeight = (el: HTMLElement): number => {
    const height = el.offsetHeight
    const style: CSSStyleDeclaration = window.getComputedStyle(el)

    return ['top', 'bottom']
        .map(
            (side: string): number =>
                parseInt(style.getPropertyValue(`margin-${side}`)) || 0
        )
        .reduce((total, side) => total + side, height)
}

/**
 * Gets the first available field from the form
 *
 * @param {HTMLElement} el
 * @return {Nullable<FormFields>} The first form field except input[type=hidden]
 */
export const getFirstFormField = (
    el: HTMLFormElement
): Nullable<FormFields> => {
    return el.querySelector<FormFields>(
        'button, select, input:not([type="hidden"]), textarea'
    )
}

/**
 * Get the page type of the current page, default to index
 */
export const getPageType = (): PageType => {
    const htmlElem = document.querySelector<HTMLElement>('html')
    let type = ''
    if (htmlElem) {
        type = htmlElem.getAttribute(`${ATTR_NAME.page.pageType}`) || ''
    }
    return PageType[type as keyof typeof PageType] || PageType.index
}

/**
 * Gets all styles from element and creates an object with property as keys
 * @param el
 */
export const getStyles = (
    el: Nullable<HTMLElement>
): Record<string, string> => {
    if (!el) {
        return {}
    }

    const styles: Record<string, string> = {}
    // get existing styles by key
    const styleAttr = el.getAttribute(HtmlAttr.STYLE)
    if (styleAttr) {
        styleAttr
            .split(';')
            .map((kv) => kv.split(':').map((_) => _.trim()))
            .forEach(([k, v]) => k && v && (styles[k] = v))
    }
    return styles
}

/**
 * Manipulates element styles (preserves existing).
 * @param {Nullable<HTMLElement>} el
 * @param {Record<string, string>} styles
 */
export const setStyles = (
    el: Nullable<HTMLElement>,
    styles: Record<string, string>
): void => {
    if (!el) {
        return
    }
    el.setAttribute(
        HtmlAttr.STYLE,
        Object.entries(styles)
            .map(([k, v]) => `${k}:${v}`)
            .join(';')
    )
}

/**
 * Check if an element overlaps target
 */
const doesElementOverlapTarget = (
    element: HTMLElement,
    target: HTMLElement
): boolean => {
    const elementBounds = element.getBoundingClientRect()
    const targetBounds = target.getBoundingClientRect()

    const hasOverlapX =
        // Is the right of element within target's bounds?
        (elementBounds.right >= targetBounds.left &&
            elementBounds.right <= targetBounds.right) ||
        // Is the left of element within target's bounds?
        (elementBounds.left >= targetBounds.left &&
            elementBounds.left <= targetBounds.right) ||
        // Is target fully contained within element's bounds?
        (targetBounds.left >= elementBounds.left &&
            targetBounds.right <= elementBounds.right)

    const hasOverlapY =
        // Is the bottom of element within target's bounds?
        (elementBounds.bottom >= targetBounds.top &&
            elementBounds.bottom <= targetBounds.bottom) ||
        // Is the top of element within target's bounds?
        (elementBounds.top >= targetBounds.top &&
            elementBounds.top <= targetBounds.bottom) ||
        // Is target fully contained within element's bounds?
        (targetBounds.top >= elementBounds.top &&
            targetBounds.bottom <= elementBounds.bottom)

    return hasOverlapX && hasOverlapY
}

/**
 * Check if an element overlaps with at least one target
 */
export const doesElementOverlapSomeTargets = (
    element: HTMLElement,
    targets: HTMLElement[]
): boolean => {
    for (const target of targets) {
        if (doesElementOverlapTarget(element, target)) {
            // Terminate as soon as we find a match
            return true
        }
    }

    return false
}
